<template>
    <CContainer class="p-0">
        <CRow alignVertical="center">
            <CCol md="12" sm="12">
                <h5>
                    {{ $t("Campaign") }}
                </h5>
                <p class="mb-1">
                    {{
                        $t(
                            "Select the campaign where you want to save the leads"
                        )
                    }}
                </p>
                <SelectAsync
                    size="sm"
                    :endpoint="'provider'"
                    :placeholder="$t('select_a_provider')"
                    :optionsValue="'uuid'"
                    :optionsLabel="'name'"
                    :value.sync="$v.settings.providerUuid.$model"
                    @change="loadProviderFields()"
                    :isValid="
                        checkIfSettingsValid({
                            fieldName: 'providerUuid',
                        })
                    "
                />
            </CCol>
        </CRow>
        <CRow alignVertical="center" v-if="settings.providerUuid">
            <CCol md="12" sm="12">
                <h5>
                    {{ $t("Pages") }}
                </h5>
                <p class="mb-1">
                    {{
                        $t(
                            "Select the Facebook page where you have the form you want to link to."
                        )
                    }}
                </p>
                <CSelect
                    size="sm"
                    :value.sync="$v.settings.pageId.$model"
                    :options="pagesPluginIntegration"
                    :placeholder="$t('select_a_page')"
                    @change="loadFormsPluginIntegration()"
                    :isValid="
                        checkIfSettingsValid({
                            fieldName: 'pageId',
                        })
                    "
                />
            </CCol>
        </CRow>
        <CRow alignVertical="center" v-if="settings.pageId">
            <CCol md="12" sm="12">
                <h5>
                    {{ $t("Forms") }}
                </h5>
                <p class="mb-1">
                    {{
                        $t(
                            "Select the form from which you want to get the leads."
                        )
                    }}
                </p>
                <CSelect
                    size="sm"
                    :value.sync="$v.settings.formId.$model"
                    :options="formsPluginIntegration"
                    :placeholder="$t('select_a_form')"
                    @change="loadFieldsFromApiFacebook()"
                    :isValid="
                        checkIfSettingsValid({
                            fieldName: 'formId',
                        })
                    "
                />
            </CCol>
        </CRow>
        <CRow v-if="settings.formId" class="mb-4">
            <CCol md="12" sm="12">
                <h5>
                    {{ $t("field_mapping") }}
                </h5>
                <p class="mb-0">
                    {{
                        $t(
                            "You can map the data coming from the Facebook form with the data from the platform itself"
                        )
                    }}.
                </p>
                <CustomTable
                    :items="settings.fieldsMapping.items"
                    :fields="settings.fieldsMapping.fields"
                    :resizable="false"
                >
                    <template #no-items-view>
                        <div class="block-no-items section">
                            <h2 class="section__title text-no-items">
                                {{ $t("Add the fields to mapping") }}
                            </h2>
                            <CButton
                                color="success"
                                class="button m-2 shadow"
                                @click="addItemToTable('fieldsMapping')"
                            >
                                <span class="button__icon">
                                    <CIcon :content="$options.icons.cilPlus" />
                                </span>
                                <span class="button__text">
                                    {{ $t("Add field") }}
                                </span>
                            </CButton>
                        </div>
                    </template>
                    <template #under-table>
                        <CButton
                            color="success"
                            class="button m-2 shadow"
                            @click="addItemToTable('fieldsMapping')"
                            v-if="settings.fieldsMapping.items.length > 0"
                        >
                            <span class="button__icon">
                                <CIcon :content="$options.icons.cilPlus" />
                            </span>
                            <span class="button__text">
                                {{ $t("Add field") }}
                            </span>
                        </CButton>
                    </template>
                    <template #value="{ index }">
                        <SelectAsync
                            :value.sync="
                                settings.fieldsMapping.items[index].value
                            "
                            :options="fieldsFromApiFacebook"
                            :placeholder="$t('Select an option')"
                            :invalidFeedback="$t('Option is requiered')"
                            size="sm"
                            class="mb-0"
                        />
                    </template>
                    <template #name="{ index }">
                        <SelectAsync
                            :value.sync="
                                settings.fieldsMapping.items[index].name
                            "
                            @change="changeFieldMapping(index)"
                            :options="fieldsProvider"
                            :placeholder="$t('Select a field')"
                            :invalidFeedback="$t('Field is requiered')"
                            size="sm"
                            class="mb-0"
                        />
                    </template>
                    <template #details="{ item, index }">
                        <CCollapse :show="Boolean(item._toggled)">
                            <CCardBody>
                                <CRow>
                                    <CCol md="12" sm="12">
                                        <h5>
                                            {{ $t("Adapter Values") }}
                                        </h5>
                                        <p class="mb-1">
                                            {{
                                                $t(
                                                    "We will update the values coming from Facebook with the values you have set in this configuration."
                                                )
                                            }}
                                        </p>
                                        <CDataTable
                                            class="overflow-visible"
                                            :items="
                                                settings.fieldsMapping.items[
                                                    index
                                                ]['fieldAdapter']
                                            "
                                            :fields="[
                                                {
                                                    key: 'name',
                                                    label: 'Facebook value',
                                                },
                                                {
                                                    key: 'value',
                                                    label: 'Leadgrease value',
                                                },
                                                {
                                                    key: 'options',
                                                    label: 'options',
                                                },
                                            ]"
                                            hover
                                            border
                                            striped
                                        >
                                            <template #no-items-view>
                                                <div
                                                    class="block-no-items section"
                                                >
                                                    <h2
                                                        class="section__title text-no-items"
                                                    >
                                                        {{
                                                            $t(
                                                                "Add a fixed field"
                                                            )
                                                        }}
                                                    </h2>
                                                    <CButton
                                                        color="success"
                                                        class="button m-2 shadow"
                                                        :title="$t('Add field')"
                                                        @click="
                                                            addItemToFieldAdapterTable(
                                                                index
                                                            )
                                                        "
                                                    >
                                                        <span
                                                            class="button__icon"
                                                        >
                                                            <CIcon
                                                                :content="
                                                                    $options
                                                                        .icons
                                                                        .cilPlus
                                                                "
                                                            />
                                                        </span>
                                                        <span
                                                            class="button__text"
                                                        >
                                                            {{
                                                                $t("Add field")
                                                            }}
                                                        </span>
                                                    </CButton>
                                                </div>
                                            </template>
                                            <template #under-table>
                                                <CButton
                                                    color="success"
                                                    class="button m-2 shadow"
                                                    @click="
                                                        addItemToFieldAdapterTable(
                                                            index
                                                        )
                                                    "
                                                    v-if="
                                                        settings.fieldsMapping
                                                            .items[index][
                                                            'fieldAdapter'
                                                        ] &&
                                                        settings.fieldsMapping
                                                            .items[index][
                                                            'fieldAdapter'
                                                        ].length > 0
                                                    "
                                                >
                                                    <span class="button__icon">
                                                        <CIcon
                                                            :content="
                                                                $options.icons
                                                                    .cilPlus
                                                            "
                                                        />
                                                    </span>
                                                    <span class="button__text">
                                                        {{ $t("Add field") }}
                                                    </span>
                                                </CButton>
                                            </template>
                                            <template #name="{ item }">
                                                <td>
                                                    <SelectAsync
                                                        v-if="
                                                            listValuesAdapterFB[
                                                                settings
                                                                    .fieldsMapping
                                                                    .items[
                                                                    index
                                                                ].value
                                                            ] &&
                                                            listValuesAdapterFB[
                                                                settings
                                                                    .fieldsMapping
                                                                    .items[
                                                                    index
                                                                ].value
                                                            ].length > 0
                                                        "
                                                        :value.sync="item.name"
                                                        :options="
                                                            listValuesAdapterFB[
                                                                settings
                                                                    .fieldsMapping
                                                                    .items[
                                                                    index
                                                                ].value
                                                            ]
                                                        "
                                                        :placeholder="
                                                            $t(
                                                                'enter_file_name'
                                                            )
                                                        "
                                                        :invalidFeedback="
                                                            $t(
                                                                'Field is requiered'
                                                            )
                                                        "
                                                        size="sm"
                                                    />
                                                    <CInput
                                                        v-else
                                                        :placeholder="
                                                            $t(
                                                                'enter_file_name'
                                                            )
                                                        "
                                                        size="sm"
                                                        :value.sync="item.name"
                                                    />
                                                </td>
                                            </template>
                                            <template #value="{ item }">
                                                <td>
                                                    <SelectAsync
                                                        v-if="
                                                            listValuesAdapterLG[
                                                                settings
                                                                    .fieldsMapping
                                                                    .items[
                                                                    index
                                                                ].name
                                                            ] &&
                                                            listValuesAdapterLG[
                                                                settings
                                                                    .fieldsMapping
                                                                    .items[
                                                                    index
                                                                ].name
                                                            ].length > 0
                                                        "
                                                        :value.sync="item.value"
                                                        :options="
                                                            listValuesAdapterLG[
                                                                settings
                                                                    .fieldsMapping
                                                                    .items[
                                                                    index
                                                                ].name
                                                            ]
                                                        "
                                                        :placeholder="
                                                            $t(
                                                                'enter_file_value'
                                                            )
                                                        "
                                                        :invalidFeedback="
                                                            $t(
                                                                'Field is requiered'
                                                            )
                                                        "
                                                        size="sm"
                                                    />
                                                    <CInput
                                                        v-else
                                                        :placeholder="
                                                            $t(
                                                                'enter_file_value'
                                                            )
                                                        "
                                                        size="sm"
                                                        :value.sync="item.value"
                                                    />
                                                </td>
                                            </template>
                                            <template #options="{ item }">
                                                <td class="py-2 text-center">
                                                    <CButton
                                                        color="danger"
                                                        variant="outline"
                                                        square
                                                        size="sm"
                                                        @click="
                                                            deleteItemToFieldAdapterTable(
                                                                index,
                                                                item.name
                                                            )
                                                        "
                                                    >
                                                        <CIcon
                                                            :content="
                                                                $options.icons
                                                                    .cilTrash
                                                            "
                                                        />
                                                    </CButton>
                                                </td>
                                            </template>
                                        </CDataTable>
                                    </CCol>
                                </CRow>
                            </CCardBody>
                        </CCollapse>
                    </template>
                    <template #options="{ index }">
                        <div class="table-options">
                            <CButton
                                color="success"
                                variant="outline"
                                square
                                size="sm"
                                @click="toggleDetails('fieldsMapping', index)"
                                class="mr-2"
                            >
                                <CIcon :content="$options.icons.cilPencil" />
                            </CButton>
                            <CButton
                                color="danger"
                                variant="outline"
                                square
                                size="sm"
                                @click="
                                    deleteItemToTable('fieldsMapping', index)
                                "
                            >
                                <CIcon :content="$options.icons.cilTrash" />
                            </CButton>
                        </div>
                    </template>
                </CustomTable>
            </CCol>
        </CRow>
        <CRow v-if="settings.formId" class="mb-4">
            <CCol md="12" sm="12">
                <h5>
                    {{ $t("Fixed fields") }}
                </h5>
                <p class="mb-0">
                    {{
                        $t(
                            "You can create fixed fields that you want to link and do not come from the form"
                        )
                    }}.
                </p>
                <TableFixedFields :value.sync="settings.fixedFields" />
            </CCol>
        </CRow>
        <CRow v-if="settings.formId">
            <CCol md="12" sm="12">
                <h5>
                    {{ $t("Dynamic fields") }}
                </h5>
                <p>
                    {{
                        $t(
                            "You can create dynamic fields using both variables and fixed text"
                        )
                    }}.
                </p>
                <p class="mb-1">
                    {{ $t("The following variables are available") }}:
                </p>
                <ul class="p-0 list-unstyled">
                    <li>
                        <strong>{{ "{" }}{{ "{" }}adId{{ "}" }}{{ "}" }}</strong
                        >:
                        {{
                            $t("Id of the ad the lead belongs to on Facebook")
                        }}.
                    </li>
                    <li>
                        <strong
                            >{{ "{" }}{{ "{" }}adName{{ "}" }}{{ "}" }}</strong
                        >:
                        {{
                            $t(
                                "Name of the ad the lead belongs to on Facebook"
                            )
                        }}.
                    </li>
                    <li>
                        <strong
                            >{{ "{" }}{{ "{" }}adsetId{{ "}" }}{{ "}" }}</strong
                        >:
                        {{
                            $t(
                                "Id of the adset the lead belongs to on Facebook"
                            )
                        }}.
                    </li>
                    <li>
                        <strong
                            >{{ "{" }}{{ "{" }}adsetName{{ "}"
                            }}{{ "}" }}</strong
                        >:
                        {{
                            $t(
                                "Name of the adset the lead belongs to on Facebook"
                            )
                        }}.
                    </li>
                    <li>
                        <strong
                            >{{ "{" }}{{ "{" }}campaignId{{ "}"
                            }}{{ "}" }}</strong
                        >:
                        {{
                            $t(
                                "Campaign ID to which the potential customer belongs on Facebook"
                            )
                        }}.
                    </li>
                    <li>
                        <strong
                            >{{ "{" }}{{ "{" }}campaignName{{ "}"
                            }}{{ "}" }}</strong
                        >:
                        {{
                            $t(
                                "Name of the campaign to which the potential customer belongs on Facebook"
                            )
                        }}.
                    </li>
                    <li>
                        <strong
                            >{{ "{" }}{{ "{" }}leadgenId{{ "}"
                            }}{{ "}" }}</strong
                        >: {{ $t("Lead id on Facebook") }}.
                    </li>
                    <li>
                        <strong
                            >{{ "{" }}{{ "{" }}pageId{{ "}" }}{{ "}" }}</strong
                        >:
                        {{
                            $t(
                                "Id of the page the lead belongs to on Facebook"
                            )
                        }}.
                    </li>
                    <li>
                        <strong
                            >{{ "{" }}{{ "{" }}formId{{ "}" }}{{ "}" }}</strong
                        >:
                        {{
                            $t(
                                "Id of the form the lead belongs to on Facebook"
                            )
                        }}.
                    </li>
                    <li>
                        <strong
                            >{{ "{" }}{{ "{" }}timestamp{{ "}"
                            }}{{ "}" }}</strong
                        >:
                        {{
                            $t(
                                "Timestamp in the lead data. The timestamp is obtained from the Facebook capture date"
                            )
                        }}.
                    </li>
                    <li>
                        <strong>{{ "{" }}{{ "{" }}ip{{ "}" }}{{ "}" }}</strong
                        >:
                        {{
                            $t(
                                "IP in the lead data. The IP address from which Facebook sends us the lead will be stored"
                            )
                        }}.
                    </li>
                </ul>
                <p class="mt-0">
                    {{ $t("example") }}: fb_<strong
                        >{{ "{" }}{{ "{" }}adName{{ "}" }}{{ "}" }}</strong
                    >_redHat
                </p>
                <TableDynamicFields :value.sync="settings.dynamicFields" />
            </CCol>
        </CRow>
    </CContainer>
</template>

<script>
import SelectAsync from "£lement/SelectAsync";
import ButtonSave from "~/components/ButtonSave.vue";
import TableFixedFields from "~/components/TableFixedFields.vue";
import TableDynamicFields from "~/components/TableDynamicFields.vue";
import CustomTable from "~/components/CustomTable";
import { cilPlus, cilTrash, cilPencil } from "@coreui/icons";
import { required } from "vuelidate/lib/validators";

import baseMixin from "~/mixins/base.js";
import { validationMixin } from "vuelidate";
import {
    FACEBOOK_ACTION_GET_FIELDS,
    FACEBOOK_ACTION_GET_FORMS,
    FACEBOOK_ACTION_GET_PAGES,
} from "~/pages/app/inputs/Integrations/Config/Types";
import options from "../../../dashboard/charts/options/options";

export default {
    name: "FacebookSettings",
    mixins: [baseMixin, validationMixin],
    icons: {
        cilPlus,
        cilTrash,
        cilPencil,
    },
    consts: {
        FACEBOOK_ACTION_GET_FIELDS,
        FACEBOOK_ACTION_GET_FORMS,
        FACEBOOK_ACTION_GET_PAGES,
    },
    options: {},
    components: {
        SelectAsync,
        ButtonSave,
        TableFixedFields,
        TableDynamicFields,
        CustomTable,
        SelectAsync,
    },
    data() {
        return {
            settings: {
                providerUuid: null,
                pageId: null,
                formId: null,
                fieldsMapping: {
                    fields: [
                        {
                            key: "value",
                            label: "Facebook field",
                        },
                        {
                            key: "name",
                            label: "Leadgrease field",
                        },
                        {
                            key: "fieldAdapter",
                            label: "Adapter Values",
                            _active: false,
                        },
                        {
                            key: "type",
                            label: "Type",
                            _active: false,
                        },
                        {
                            key: "options",
                            label: "Options",
                        },
                    ],
                    items: [],
                },
            },
            pagesPluginIntegration: [],
            formsPluginIntegration: [],
            fieldsFromApiFacebook: [],
            fieldsProvider: [],
            listValuesAdapterLG: [],
            listValuesAdapterFB: [],
        };
    },
    validations: {
        settings: {
            providerUuid: {
                required,
            },
            pageId: {
                required,
            },
            formId: {
                required,
            },
        },
    },
    computed: {},
    props: {
        plugin_marketing_credentials_uuid: {
            required: true,
        },
        value: {
            default: () => ({
                providerUuid: null,
                pageId: null,
                formId: null,
                fieldsMapping: {
                    fields: [
                        {
                            key: "value",
                            label: "Value",
                        },
                        {
                            key: "name",
                            label: "Name",
                        },
                        {
                            key: "type",
                            label: "Type",
                            _active: false,
                        },
                        {
                            key: "fieldAdapter",
                            label: "Adapter Values",
                            _active: false,
                        },
                        {
                            key: "options",
                            label: "Options",
                        },
                    ],
                    items: [],
                },
            }),
            required: true,
        },
    },
    watch: {
        async value(newValue) {
            await this.assingValues(newValue);
        },
        "settings.providerUuid": {
            handler(newValue) {
                const updateValue = {
                    ...this.value,
                    providerUuid: newValue,
                };
                this.$emit("update:value", updateValue);
                this.$emit("change", updateValue);
            },
            deep: true,
        },
        "settings.pageId": {
            handler(newValue) {
                const updateValue = {
                    ...this.value,
                    pageId: newValue,
                };
                this.$emit("update:value", updateValue);
                this.$emit("change", updateValue);
            },
            deep: true,
        },
        "settings.formId": {
            handler(newValue) {
                const updateValue = {
                    ...this.value,
                    formId: newValue,
                };
                this.$emit("update:value", updateValue);
                this.$emit("change", updateValue);
            },
            deep: true,
        },
        "settings.fieldsMapping.items": {
            handler(newValue) {
                const updateValue = {
                    ...this.value,
                    fieldsMapping: newValue,
                };
                this.$emit("update:value", updateValue);
                this.$emit("change", updateValue);
            },
            deep: true,
        },
        "settings.fixedFields": {
            handler(newValue) {
                const updateValue = {
                    ...this.value,
                    fixedFields: newValue,
                };
                this.$emit("update:value", updateValue);
                this.$emit("change", updateValue);
            },
            deep: true,
        },
        "settings.dynamicFields": {
            handler(newValue) {
                const updateValue = {
                    ...this.value,
                    dynamicFields: newValue,
                };
                this.$emit("update:value", updateValue);
                this.$emit("change", updateValue);
            },
            deep: true,
        },
    },
    methods: {
        async assingValues(newValue) {
            if (newValue.providerUuid != undefined)
                this.settings.providerUuid = newValue.providerUuid;
            if (newValue.fieldsMapping != undefined)
                this.settings.fieldsMapping.items = newValue.fieldsMapping;
            if (newValue.fixedFields != undefined)
                this.settings.fixedFields = newValue.fixedFields;
            if (newValue.dynamicFields != undefined)
                this.settings.dynamicFields = newValue.dynamicFields;
            if (newValue.pageId != undefined)
                this.settings.pageId = newValue.pageId;
            if (newValue.formId != undefined)
                this.settings.formId = newValue.formId;
        },
        async loadPagesPluginIntegration() {
            let pages = await this.fetcher({
                url: `/connector.api/integrations/${this.plugin_marketing_credentials_uuid}/actions/${this.$options.consts.FACEBOOK_ACTION_GET_PAGES}/execute`,
            });

            if (pages["pluginActionResponse"].length > 0) {
                return pages["pluginActionResponse"].map((page) => {
                    this.pagesPluginIntegration.push({
                        value: page.id,
                        label: page.name,
                    });
                });
            }
        },
        async loadFormsPluginIntegration() {
            this.formsPluginIntegration = [];
            if (this.settings.pageId) {
                let forms = await this.fetcher({
                    url: `/connector.api/integrations/${this.plugin_marketing_credentials_uuid}/actions/${this.$options.consts.FACEBOOK_ACTION_GET_FORMS}/execute?pageId=${this.settings.pageId}`,
                });

                if (forms["pluginActionResponse"].length > 0) {
                    return forms["pluginActionResponse"].map((form) => {
                        this.formsPluginIntegration.push({
                            value: form.id,
                            label: form.name,
                        });
                    });
                }
            }
        },
        async loadFieldsFromApiFacebook() {
            if (this.settings.pageId && this.settings.formId) {
                let data = await this.fetcher({
                    url: `/connector.api/integrations/${this.plugin_marketing_credentials_uuid}/actions/${this.$options.consts.FACEBOOK_ACTION_GET_FIELDS}/execute?pageId=${this.settings.pageId}&formId=${this.settings.formId}`,
                });
                this.fieldsFromApiFacebook =
                    data["pluginActionResponse"]["names"];
                this.listValuesAdapterFB =
                    data["pluginActionResponse"]["options"];
            }
        },
        async loadProviderFields() {
            if (this.settings.providerUuid) {
                this.fieldsProvider = [];
                this.listValuesAdapterLG = [];
                let data = await this.fetcher({
                    url: `/input.api/providers/${this.settings.providerUuid}/fields`,
                });
                let field;
                data._items.map((item) => {
                    field = this.findFieldProvider(item.field_id);
                    let options = null;
                    if (field.options) {
                        options = field.options.split(",\n");
                    }
                    if (item.mapping) {
                        this.fieldsProvider.push(item.mapping);
                        if (field)
                            this.listValuesAdapterLG[item.mapping] = options;
                    } else {
                        if (field) {
                            this.fieldsProvider.push(field.name);
                            this.listValuesAdapterLG[field.name] = options;
                        }
                    }
                });
            }
        },
        findFieldProvider(fieldId) {
            return this.storeGlobalFieldsAdvance.find((field) => {
                return field.id == fieldId;
            });
        },
        getDefaultTableItem(tableName) {
            let item = {};
            this.settings[tableName].fields.forEach((field) => {
                if (field.key != "options")
                    item = {
                        ...item,
                        [field.key]: null,
                    };
            });
            return item;
        },
        addItemToTable(tableName) {
            let defaultItem = this.getDefaultTableItem(tableName);
            let items = [...this.settings[tableName].items];
            items.push(defaultItem);
            this.$set(this.settings[tableName], "items", items);
        },
        deleteItemToTable(tableName, index) {
            this.settings[tableName].items.splice(index, 1);
        },
        validateSettings() {
            let valForm = this.$v.settings;
            if (!valForm) return false;
            valForm.$touch();
            return !valForm.$invalid;
        },
        checkIfSettingsValid({ fieldName }) {
            const field = this.$v.settings[fieldName];
            if (!field.$dirty) {
                return null;
            }
            return !(field.$invalid || field.$model === "");
        },
        async changeFieldMapping(index) {
            const field = await this.getTypeField(
                this.settings.fieldsMapping.items[index].name
            );

            if (field) {
                return (this.settings.fieldsMapping.items[index].type =
                    field.type);
            }

            return null;
        },
        async getTypeField(name) {
            return this.storeGlobalFieldsAdvance.find((field) => {
                return field.name == name;
            });
        },
        toggleDetails(tableName, index) {
            let item = this.settings[tableName].items[index];
            this.$set(
                this.settings[tableName].items[index],
                "_toggled",
                !item._toggled
            );
        },
        addItemToFieldAdapterTable(index) {
            let mappingItems = [...this.settings.fieldsMapping.items];
            if (!mappingItems[index].fieldAdapter) {
                mappingItems[index] = {
                    ...mappingItems[index],
                    fieldAdapter: [],
                };
            }
            if (Array.isArray(mappingItems[index].fieldAdapter)) {
                mappingItems[index].fieldAdapter.push({
                    name: null,
                    value: null,
                });
            } else {
                mappingItems[index].fieldAdapter = [
                    {
                        name: null,
                        value: null,
                    },
                ];
            }

            this.settings.fieldsMapping.items = mappingItems;
        },
        deleteItemToFieldAdapterTable(index, adapterName) {
            let findAdapter = this.settings.fieldsMapping.items[
                index
            ].fieldAdapter.findIndex((adapter) => adapter.name == adapterName);

            if (findAdapter == 0) {
                this.settings.fieldsMapping.items[index].fieldAdapter.splice(
                    0,
                    1
                );
            } else if (findAdapter > 0) {
                this.settings.fieldsMapping.items[index].fieldAdapter.splice(
                    findAdapter,
                    1
                );
            }
        },
    },
    async mounted() {
        await this.assingValues(this.value);
        await this.loadPagesPluginIntegration();
        await this.loadFormsPluginIntegration();
        await this.loadFieldsFromApiFacebook();
        await this.loadProviderFields();
    },
    async created() {
        await this.loadFieldsAdvance();
    },
};
</script>

<style>
.overflow-visible .table-responsive {
    overflow: visible;
}
</style>
